import Bugsnag from '@bugsnag/js';
import Head from 'next/head';
import { useStoreDispatch } from 'store/hooks/storeHooks';

import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { getSearchResults } from 'server/utils/constructor/getSearchResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { t } from 'localization/utils/t';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import ProductSearchGrid from 'shared/components/product-grid';
import { setIsHeaderVisible } from 'store/slice/pageLayoutSlice';
import { SearchGrid } from 'search/components/searchGrid/SearchGrid';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

const TP = 'pages.search';

const SearchPage = ({ constructorResponse, searchQuery, globalPricingId, isCrawler = false }) => {
  const browseKey = constructorResponse?.request?.browse_filter_name || 'group_id';
  const browseValue = constructorResponse?.request?.browse_filter_value || 'all';
  const { pageTitle } = usePageMetadata({
    defaultTitle: searchQuery
      ? t(`${TP}.titleSearch`, 'Search | {query} ', { query: `'${searchQuery as string}'` })
      : t(`${TP}.titleShopAll`, 'Shop All '),
  });
  const storeDispatch = useStoreDispatch();
  const toggleHeader = (status) => {
    storeDispatch(setIsHeaderVisible(status));
  };
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  return (
    <>
      <Head>
        <title key="title">{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="og:title" />
      </Head>
      {!isNewSearchPageEnabled ? (
        <SearchGrid
          browseKey={browseKey}
          browseValue={browseValue}
          constructorResponse={constructorResponse}
          globalPricingId={globalPricingId}
          isCrawler={isCrawler}
        />
      ) : (
        <div style={{ marginTop: 0, overflow: 'hidden' }}>
          <ProductSearchGrid
            toggleHeader={toggleHeader}
            collectionSlug=""
            collectionDetails={undefined}
            isCrawler={isCrawler}
            pageAttributes={{
              isSearchPath: true,
            }}
          />
        </div>
      )}
      {/* </ErrorBoundary> */}
    </>
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const { browseKey, browseValue, query: searchQuery } = query;
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const isCrawler = isSearchCrawler(req?.headers);
  const isConsumerSearchEnabled =
    req.featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  if (isConsumerSearchEnabled) {
    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        ...(searchQuery ? { searchQuery } : {}),
        globalPricingId,
        isCrawler,
      },
    };
  }

  if (searchQuery) {
    try {
      const searchResponse = await getSearchResults({
        req,
        res,
        resolvedUrl,
        query,
        searchQuery,
      });

      return {
        props: {
          ...(await setServerSideTranslations({ locale })),
          constructorResponse: searchResponse,
          searchQuery,
          globalPricingId,
          isCrawler,
        },
      };
    } catch (error) {
      if (typeof error !== 'undefined') {
        Bugsnag.notify(error, (event) => {
          event.context = 'Fetching Search Items';
        });
      }
      return { notFound: true };
    }
  }

  try {
    const filterKey: string = browseKey || 'group_id';
    const filterValue: string = browseValue || 'all';

    const browseResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey,
      filterValue,
    });

    const invalidSearch = browseResponse.response.total_num_results === 0;

    if (invalidSearch) {
      if (filterKey === 'group_id' && filterValue === 'all' && !browseResponse?.request?.filters) {
        Bugsnag.notify(new Error('Constructor returning 0 results for browse request'), (event) =>
          event.addMetadata('search', {
            filterKey,
            filterValue,
            resultId: browseResponse?.result_id,
          }),
        );
      }

      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        constructorResponse: browseResponse,
        globalPricingId,
        isCrawler,
      },
    };
  } catch (error) {
    if (typeof error !== 'undefined') {
      Bugsnag.notify(error, (event) => {
        event.context = 'Fetching Browse Items';
      });
    }
    return { notFound: true };
  }
};

export default SearchPage;
